import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { FilmEngine } from '@/components/landing/film/engine/engine'
import type { FilmFrame } from '@/components/landing/film/frame'
import type { FilmFormat } from '@/components/landing/film/film-controls'
import type { OverlayUpdater, RegisterUpdater } from '@/components/landing/film/overlay-utils'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createEmptyFrame } from '@/components/landing/film/frame'
import { fillFallbackFrame } from '@/components/landing/film/overlay-utils'
import FilmOverlay from '@/components/landing/film/film-overlay'
import FilmControls from '@/components/landing/film/film-controls'

import {
    FILM_DURATION,
    SCENES,
    clamp,
    sceneIndexAt
} from '@/components/landing/film/timeline'

const FilmCanvas = dynamic(() => import('@/components/landing/film/film-canvas'), { ssr: false })

interface FilmClock {
    t: number
    playing: boolean
    ended: boolean
    idle: number
    last: number
    seeked: boolean
    autoPaused: boolean
    visible: boolean
    ready: boolean
    scene: number
}

export const FILM_SEEK_EVENT = 'film:seek'

// Ask the film to jump to a moment, from anywhere on the page
export const seekFilm = (time: number): void => {
    window.dispatchEvent(new CustomEvent(FILM_SEEK_EVENT, { detail: { time } }))
    document.getElementById('film')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const FilmStage: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const engineRef = useRef<FilmEngine | null>(null)
    const updaters = useRef(new Set<OverlayUpdater>())
    const fallbackFrame = useRef<FilmFrame>(createEmptyFrame())
    const size = useRef({ width: 1, height: 1 })
    const clock = useRef<FilmClock>({
        t: 0,
        playing: false,
        ended: false,
        idle: 0,
        last: 0,
        seeked: true,
        autoPaused: false,
        visible: true,
        ready: false,
        scene: 0
    })

    const [playing, setPlaying] = useState(false)
    const [ended, setEnded] = useState(false)
    const [sceneIndex, setSceneIndex] = useState(0)
    const [format, setFormat] = useState<FilmFormat>('cinema')
    const [failed, setFailed] = useState(false)
    const [booted, setBooted] = useState(false)

    const register = useCallback<RegisterUpdater>((updater) => {
        updaters.current.add(updater)
        return () => {
            updaters.current.delete(updater)
        }
    }, [])

    // Starts playback once the 3D engine reports in (or gives up). Safe to call in any effect order.
    const start = useCallback(() => {
        const c = clock.current
        if (c.ready) return
        c.ready = true
        setBooted(true)
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (!reduced && c.t < FILM_DURATION) {
            c.playing = true
            c.seeked = true
            setPlaying(true)
        }
    }, [])

    const handleReady = useCallback((engine: FilmEngine) => {
        engineRef.current = engine
        start()
    }, [start])

    const handleFailure = useCallback(() => {
        engineRef.current = null
        setFailed(true)
        start()
    }, [start])

    const seek = useCallback((time: number, play = true) => {
        const c = clock.current
        c.t = clamp(time, 0, FILM_DURATION)
        c.seeked = true
        c.idle = 0
        c.ended = c.t >= FILM_DURATION
        c.playing = play && !c.ended
        c.autoPaused = false
        setEnded(c.ended)
        setPlaying(c.playing)
    }, [])

    const replay = useCallback(() => seek(0, true), [seek])

    const toggle = useCallback(() => {
        const c = clock.current
        if (c.ended) {
            seek(0, true)
            return
        }
        c.playing = !c.playing
        c.autoPaused = false
        c.seeked = true
        setPlaying(c.playing)
    }, [seek])

    const pause = useCallback(() => {
        const c = clock.current
        c.playing = false
        setPlaying(false)
    }, [])

    useEffect(() => {
        const c = clock.current
        if (window.matchMedia('(orientation: portrait)').matches) setFormat('vertical')
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            c.t = FILM_DURATION
            c.ended = true
            c.playing = false
            c.scene = SCENES.length - 1
            setEnded(true)
            setPlaying(false)
            setSceneIndex(SCENES.length - 1)
        }
        const timer = window.setTimeout(start, 3000)
        return () => window.clearTimeout(timer)
    }, [start])

    useEffect(() => {
        const frameElement = frameRef.current
        if (!frameElement) return
        const update = (): void => {
            const rect = frameElement.getBoundingClientRect()
            size.current = { width: rect.width, height: rect.height }
        }
        update()
        const observer = new ResizeObserver(update)
        observer.observe(frameElement)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const section = sectionRef.current
        if (!section) return
        const c = clock.current
        const observer = new IntersectionObserver(([entry]) => {
            c.visible = entry.isIntersecting
            if (!entry.isIntersecting && c.playing) {
                c.playing = false
                c.autoPaused = true
            }
            else if (entry.isIntersecting && c.autoPaused) {
                c.autoPaused = false
                c.playing = true
                c.seeked = true
            }
        }, { threshold: 0.2 })
        observer.observe(section)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const handler = (event: Event): void => {
            const detail = (event as CustomEvent<{ time?: number }>).detail
            if (typeof detail?.time === 'number') seek(detail.time, true)
        }
        window.addEventListener(FILM_SEEK_EVENT, handler)
        return () => window.removeEventListener(FILM_SEEK_EVENT, handler)
    }, [seek])

    useEffect(() => {
        let raf = 0
        const c = clock.current

        const tick = (now: number): void => {
            raf = requestAnimationFrame(tick)
            const dt = c.last ? Math.min(0.1, (now - c.last) / 1000) : 1 / 60
            c.last = now
            if (!c.visible || document.hidden) return

            const before = c.t
            if (c.playing) {
                c.t = Math.min(FILM_DURATION, c.t + dt)
                if (c.t >= FILM_DURATION) {
                    c.playing = false
                    c.ended = true
                    setPlaying(false)
                    setEnded(true)
                }
            }
            if (c.ended) c.idle += dt
            const filmDelta = c.seeked ? 0 : c.t - before
            c.seeked = false

            const real = now / 1000
            let frame = fillFallbackFrame(fallbackFrame.current, size.current.width, size.current.height)
            const engine = engineRef.current
            if (engine) {
                try {
                    frame = engine.render(c.t, real, c.idle, dt, filmDelta)
                }
                catch (error) {
                    console.error('The 3D film stopped rendering.', error)
                    engineRef.current = null
                    setFailed(true)
                }
            }

            updaters.current.forEach((updater) => updater(c.t, real, frame))

            const index = sceneIndexAt(c.t)
            if (index !== c.scene) {
                c.scene = index
                setSceneIndex(index)
            }
        }

        raf = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf)
    }, [])

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.target !== event.currentTarget) return
        if (event.key === ' ' || event.key === 'k') {
            event.preventDefault()
            toggle()
        }
        if (event.key === 'ArrowRight') seek(clock.current.t + 3, clock.current.playing)
        if (event.key === 'ArrowLeft') seek(clock.current.t - 3, clock.current.playing)
    }

    return (
        <section ref={sectionRef} id='film' aria-label='Brand film' className='relative flex min-h-[100svh] items-center justify-center bg-obsidian pt-16'>
            <div className='carbon-weave pointer-events-none absolute inset-0 opacity-70' aria-hidden='true' />

            <h1 className='sr-only'>
                Alif Lamato, bespoke forged wheels
            </h1>
            <p className='sr-only'>
                A 24 second film. The name Alif Lamato forms in a dark studio, followed by a 10-spoke deep concave forged monoblock wheel in 6061-T6 aerospace aluminum, custom offsets from ET -15 to +45 and a weight of 8.4 kg. The wheel turns a full 360 degrees with its floating titanium center cap engraved ALIF LAMATO SIGNATURE, then appears in Brushed Champagne Gold, Dark Satin Carbon and Raw Polished Mirror Lip, before locking into place under the signature of Alif Lamato, wheel alchemist and architect.
            </p>

            <div
                ref={frameRef}
                data-format={format}
                role='region'
                aria-label='Film player. Press space to play or pause.'
                tabIndex={0}
                onKeyDown={handleKeyDown}
                className='film-frame shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] outline-none ring-gold/60 focus-visible:ring-2'
            >
                {failed ? (
                    <div className='absolute inset-0 bg-obsidian'>
                        <img
                            src='/images/film-poster.jpg'
                            alt=''
                            className='size-full object-cover opacity-70'
                        />
                        <div className='absolute inset-0 bg-radial from-transparent via-obsidian/30 to-obsidian/90' />
                    </div>
                ) : (
                    <FilmCanvas onReady={handleReady} onFailure={handleFailure} />
                )}

                <FilmOverlay register={register} onCta={pause} />

                {!booted && (
                    <div className='pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-4' aria-live='polite'>
                        <span className='h-px w-24 overflow-hidden bg-white/10'>
                            <span className='block h-full w-1/3 animate-[loader-slide_1.2s_ease-in-out_infinite] bg-gold' />
                        </span>
                        <span className='font-mono text-[10px] uppercase tracking-[0.3em] text-titanium'>
                            Preparing the atelier
                        </span>
                    </div>
                )}

                <FilmControls
                    register={register}
                    playing={playing}
                    ended={ended}
                    sceneIndex={sceneIndex}
                    format={format}
                    onToggle={toggle}
                    onReplay={replay}
                    onSeek={(time) => seek(time, true)}
                    onFormat={setFormat}
                />
            </div>
        </section>
    )
}

export default FilmStage
